import React from "react";
import { Card } from "../components/ui/card";

export function PageHeader({ title, description, actions }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">{title}</h1>
        {description && <p className="text-sm text-muted-foreground mt-1">{description}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

export function EmptyState({ title = "No data yet", description, action }) {
  return (
    <div className="text-center py-16 border border-dashed rounded-xl bg-muted/20">
      <div className="text-lg font-medium">{title}</div>
      {description && <div className="text-sm text-muted-foreground mt-1">{description}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function LoadingRows({ rows = 5 }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="skeleton h-10 rounded-md" />
      ))}
    </div>
  );
}

export function KPICard({ label, value, hint, icon: Icon, accent = "blue", testid }) {
  const colors = {
    blue: "text-blue-400 bg-blue-500/10",
    emerald: "text-emerald-400 bg-emerald-500/10",
    amber: "text-amber-400 bg-amber-500/10",
    fuchsia: "text-fuchsia-400 bg-fuchsia-500/10",
    slate: "text-slate-300 bg-slate-500/10",
    rose: "text-rose-400 bg-rose-500/10",
  };
  return (
    <Card data-testid={testid} className="kpi-card p-4 border rounded-xl">
      <div className="flex items-start justify-between">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
        {Icon && (
          <div className={`w-8 h-8 rounded-lg grid place-items-center ${colors[accent] || colors.blue}`}>
            <Icon className="w-4 h-4" />
          </div>
        )}
      </div>
      <div className="mt-2 font-heading text-2xl font-semibold tracking-tight">{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </Card>
  );
}

export function StatusBadge({ status }) {
  const map = {
    active: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
    pending: "bg-amber-500/10 text-amber-400 border-amber-500/30",
    installed: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
    scheduled: "bg-blue-500/10 text-blue-400 border-blue-500/30",
    not_installed: "bg-slate-500/10 text-slate-300 border-slate-500/30",
    in_inventory: "bg-slate-500/10 text-slate-300 border-slate-500/30",
    allocated: "bg-blue-500/10 text-blue-400 border-blue-500/30",
    maintenance: "bg-amber-500/10 text-amber-400 border-amber-500/30",
    retired: "bg-slate-500/10 text-slate-400 border-slate-500/30",
    suspended: "bg-rose-500/10 text-rose-400 border-rose-500/30",
    overdue: "bg-rose-500/10 text-rose-400 border-rose-500/30",
    cancelled: "bg-slate-500/10 text-slate-400 border-slate-500/30",
    paid: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
    open: "bg-amber-500/10 text-amber-400 border-amber-500/30",
    assigned: "bg-blue-500/10 text-blue-400 border-blue-500/30",
    in_progress: "bg-blue-500/10 text-blue-400 border-blue-500/30",
    resolved: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
    closed: "bg-slate-500/10 text-slate-300 border-slate-500/30",
    approved: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
    rejected: "bg-rose-500/10 text-rose-400 border-rose-500/30",
    under_warranty: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
    expired: "bg-slate-500/10 text-slate-400 border-slate-500/30",
    inactive: "bg-slate-500/10 text-slate-400 border-slate-500/30",
    paused: "bg-amber-500/10 text-amber-400 border-amber-500/30",
    terminated: "bg-rose-500/10 text-rose-400 border-rose-500/30",
    low: "bg-slate-500/10 text-slate-300 border-slate-500/30",
    medium: "bg-blue-500/10 text-blue-400 border-blue-500/30",
    high: "bg-amber-500/10 text-amber-400 border-amber-500/30",
    critical: "bg-rose-500/10 text-rose-400 border-rose-500/30",
  };
  const cls = map[status] || map.pending;
  return <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${cls}`}>{(status || "").replace(/_/g, " ")}</span>;
}

export function toCsv(rows, filename = "export.csv") {
  if (!rows?.length) return;
  const cols = Object.keys(rows[0]);
  const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => escape(r[c])).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}
